import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
} from "react";

import { cn } from "../lib/cn";

export type ShareActionButtonVariant = "icon-only" | "full";

export interface ShareActionButtonProps
  extends ComponentPropsWithoutRef<"button"> {
  variant: ShareActionButtonVariant;
  icon: ReactNode;
  title?: string;
  subtitle?: string;
  srLabel: string;
  "data-testid"?: string;
}

export const ShareActionButton = forwardRef<
  HTMLButtonElement,
  ShareActionButtonProps
>(
  (
    { variant, icon, title, subtitle, srLabel, className, disabled, ...props },
    ref,
  ) => {
    const { "data-testid": dataTestId, ...restProps } = props;
    const resolvedTestId = dataTestId ?? "share-action-button";

    if (variant === "icon-only") {
      return (
        <button
          ref={ref}
          disabled={disabled}
          data-testid={resolvedTestId}
          className={cn(
            "flex size-10 items-center justify-center rounded-full bg-twi-gray-800 text-white transition-colors",
            "hover:bg-twi-gray-700",
            "disabled:pointer-events-none disabled:opacity-50",
            className,
          )}
          {...restProps}
        >
          {icon}
          <span className="sr-only">{srLabel}</span>
        </button>
      );
    }

    return (
      <button
        ref={ref}
        disabled={disabled}
        data-testid={resolvedTestId}
        className={cn(
          "flex items-center gap-4 rounded-xl bg-twi-gray-800 px-4 py-3 text-left transition-colors",
          "hover:bg-twi-gray-700",
          "disabled:pointer-events-none disabled:opacity-50",
          className,
        )}
        {...restProps}
      >
        {icon}
        <div className="flex flex-col">
          <span className="font-semibold text-base text-white leading-6">
            {title}
          </span>
          {subtitle ? (
            <span className="font-medium text-sm text-twi-gray-300 leading-[21px]">
              {subtitle}
            </span>
          ) : null}
        </div>
      </button>
    );
  },
);

ShareActionButton.displayName = "ShareActionButton";
